import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faThumbsUp,
  faTimes,
  faPaperPlane,
  faCheck,
  faComment
} from '@fortawesome/free-solid-svg-icons';
import './OpinionModal.css';

interface OpinionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: string) => Promise<void>;
  title?: string;
  description?: string;
  placeholder?: string;
  submitButtonText?: string;
  skipButtonText?: string;
  maxLength?: number;
}

export const OpinionModal: React.FC<OpinionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title = "Podziel się opinią",
  description = "Czy odpowiedź była pomocna? Twoja opinia pomoże nam ulepszać system.",
  placeholder = "Napisz swoją opinię o otrzymanej odpowiedzi...",
  submitButtonText = "Wyślij opinię",
  skipButtonText = "Pomiń",
  maxLength = 1000
}) => {
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus management
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      // Focus the textarea after modal animation completes
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Escape key handling
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, isSubmitting, onClose]);

  // Click outside to close
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!feedback.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(feedback.trim());
      setIsSuccess(true);
      
      // Auto-close after success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFeedback('');
    setIsSubmitting(false);
    setIsSuccess(false);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const remainingChars = maxLength - feedback.length;
  const isOverLimit = remainingChars < 0;

  if (!isOpen) return null;

  return (
    <div 
      className="radonek-opinion-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="opinion-modal-title"
      aria-describedby="opinion-modal-description"
    >
      <div 
        ref={modalRef}
        className="radonek-opinion-modal-container"
        role="document"
      >
        <div className="radonek-opinion-modal-header">
          <h3 id="opinion-modal-title" className="radonek-opinion-modal-title">
            <FontAwesomeIcon icon={faThumbsUp} className="fa-icon" />
            {title}
          </h3>
          <button
            className="radonek-opinion-modal-close"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Zamknij okno opinii"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="radonek-opinion-modal-body">
          {isSuccess ? (
            <div className="radonek-opinion-modal-success">
              <FontAwesomeIcon icon={faCheck} className="radonek-opinion-success-icon" />
              <p className="radonek-opinion-success-text">
                Dziękujemy za Twoją opinię! Pomoże nam to ulepszać system.
              </p>
            </div>
          ) : (
            <>
              <p id="opinion-modal-description" className="radonek-opinion-modal-description">
                {description}
              </p>
              
              <textarea
                ref={textareaRef}
                className="radonek-opinion-textarea"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={isSubmitting}
                maxLength={maxLength}
                aria-describedby="char-count"
              />
              
              <div 
                id="char-count"
                className={`radonek-opinion-char-count ${
                  remainingChars < 50 ? (isOverLimit ? 'error' : 'warning') : ''
                }`}
              >
                {remainingChars} znaków pozostało
              </div>
            </>
          )}
        </div>

        {!isSuccess && (
          <div className="radonek-opinion-modal-footer">
            <button
              className="radonek-opinion-btn radonek-opinion-btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faTimes} className="fa-icon" />
              {skipButtonText}
            </button>
            
            <button
              className="radonek-opinion-btn radonek-opinion-btn-primary"
              onClick={handleSubmit}
              disabled={!feedback.trim() || isSubmitting || isOverLimit}
            >
              <FontAwesomeIcon 
                icon={isSubmitting ? faComment : faPaperPlane} 
                className="fa-icon" 
              />
              {isSubmitting ? 'Wysyłanie...' : submitButtonText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpinionModal;